import { ref, computed } from 'vue'
import { suratKeluarAPI } from '@/services/axios/perki'
import moment from 'moment'

/**
 * Composable for handling Nomor Surat generation and management
 * Used in Surat Keluar create/edit forms
 * Supports all surat types with auto-numbering (except "Lainnya")
 */
export function useNomorSurat() {
  // State
  const nomorSurat = ref('')
  const baseNomorSurat = ref(1)
  const initialNomorSurat = ref(1) // Track the initial value to prevent decrementing below it
  const latestNomorFromBackend = ref(null)
  const nomorSuratIncremented = ref(false)
  const loading = ref(false)
  const currentJenisSurat = ref('Surat Tugas') // Track current jenis surat

  /**
   * Map jenis surat to format code
   * @param {string} jenisSurat
   * @returns {Object|null} - { prefix, code } or null for "Lainnya"
   */
  const getFormatCode = (jenisSurat) => {
    const formatMap = {
      'Surat Tugas': { prefix: 'PP/INT', code: 'TUG' },
      'Cabang': { prefix: 'PP/INT', code: 'CAB' },
      'Farmasi': { prefix: 'PP/EKS', code: 'FAR' },
      'Instansi Pemerintah': { prefix: 'PP/EKS', code: 'PEM' },
      'Internal - Surat': { prefix: 'PP/INT', code: 'SUR' },
      'Internal - Undangan': { prefix: 'PP/INT', code: 'UND' },
      'Kepegawaian PP': { prefix: 'PP/INT', code: 'PEG' },
      'MoU': { prefix: 'PP/INT', code: 'MoU' },
      'PKS': { prefix: 'PP/INT', code: 'PKS' },
      'OPKeseminatan Lain': { prefix: 'PP/EKS', code: 'ORG' },
      'PB IDI': { prefix: 'PP/EKS', code: 'IDI' },
      'Perusahaan Swasta': { prefix: 'PP/EKS', code: 'SWA' },
      'Podcast': { prefix: 'PP/INT', code: 'POD' },
      'Pokja': { prefix: 'PP/INT', code: 'POK' },
      'Rumah Sakit': { prefix: 'PP/EKS', code: 'RS' },
      'SK': { prefix: 'PP/INT', code: 'SK' },
      'YJI': { prefix: 'PP/EKS', code: 'YJI' },
    }
    return formatMap[jenisSurat] || null
  }

  /**
   * Extract number from nomor surat string
   * @param {string} nomorSurat - e.g., "001/PP/INT/TUG/XI/2025"
   * @returns {number} - e.g., 1
   */
  const extractNumberFromNomorSurat = (nomorSuratStr) => {
    if (!nomorSuratStr) return 1
    const match = nomorSuratStr.match(/^(\d+)\//)
    return match ? parseInt(match[1], 10) : 1
  }

  /**
   * Generate nomor surat based on number, jenis surat, and current date
   * Format: XXX/PREFIX/CODE/ROMAN_MONTH/YYYY
   * @param {number} number - The sequential number
   * @param {string} jenisSurat - The surat type
   * @returns {string} - Formatted nomor surat
   */
  const generateNomorSurat = (number, jenisSurat = 'Surat Tugas') => {
    const format = getFormatCode(jenisSurat)
    if (!format) return '' // For "Lainnya" - no auto-generation

    const now = moment()
    const month = now.format('M')
    const year = now.format('YYYY')

    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']
    const romanMonth = romanNumerals[parseInt(month) - 1]

    const paddedNumber = number.toString().padStart(3, '0') // Changed to 3 digits (001 instead of 01)
    return `${paddedNumber}/${format.prefix}/${format.code}/${romanMonth}/${year}`
  }

  /**
   * Fetch latest nomor surat from backend and set initial value
   * @param {string} jenisSurat - The surat type
   * @returns {Promise<void>}
   */
  const fetchLatestNomorSurat = async (jenisSurat = 'Surat Tugas') => {
    currentJenisSurat.value = jenisSurat

    // If "Lainnya", skip fetching (uses manual input)
    if (jenisSurat === 'Lainnya') {
      nomorSurat.value = ''
      baseNomorSurat.value = 1
      initialNomorSurat.value = 1
      loading.value = false
      return
    }

    loading.value = true
    try {
      const response = await suratKeluarAPI.getLatestNomorSurat(jenisSurat)
      if (response.data?.data?.latestNomorSurat) {
        latestNomorFromBackend.value = response.data.data.latestNomorSurat
        // Extract number and set baseline to next number
        const latestNumber = extractNumberFromNomorSurat(response.data.data.latestNomorSurat)
        baseNomorSurat.value = latestNumber + 1
        initialNomorSurat.value = latestNumber + 1 // Store the initial starting value
        nomorSurat.value = generateNomorSurat(baseNomorSurat.value, jenisSurat)
      } else {
        // No previous surat, start from 1
        baseNomorSurat.value = 1
        initialNomorSurat.value = 1 // Store the initial starting value
        nomorSurat.value = generateNomorSurat(1, jenisSurat)
      }
    } catch (error) {
      console.error('Error fetching latest nomor surat:', error)
      // Fallback to default
      baseNomorSurat.value = 1
      initialNomorSurat.value = 1 // Store the initial starting value
      nomorSurat.value = generateNomorSurat(1, jenisSurat)
    } finally {
      loading.value = false
    }
  }

  /**
   * Increment nomor surat by 1
   * @param {string} jenisSurat - The surat type
   */
  const incrementNomorSurat = (jenisSurat = currentJenisSurat.value) => {
    baseNomorSurat.value++
    nomorSuratIncremented.value = true
    nomorSurat.value = generateNomorSurat(baseNomorSurat.value, jenisSurat)
  }

  /**
   * Decrement nomor surat by 1 (if possible)
   * Cannot go below the initial value to prevent using already-taken numbers
   * @param {string} jenisSurat - The surat type
   */
  const decrementNomorSurat = (jenisSurat = currentJenisSurat.value) => {
    if (baseNomorSurat.value > initialNomorSurat.value) {
      baseNomorSurat.value--
      nomorSurat.value = generateNomorSurat(baseNomorSurat.value, jenisSurat)
      // Reset increment flag if we're back to initial value
      if (baseNomorSurat.value === initialNomorSurat.value) {
        nomorSuratIncremented.value = false
      }
    }
  }

  /**
   * Reset nomor surat state
   */
  const resetNomorSurat = () => {
    nomorSurat.value = ''
    baseNomorSurat.value = 1
    initialNomorSurat.value = 1
    latestNomorFromBackend.value = null
    nomorSuratIncremented.value = false
    currentJenisSurat.value = 'Surat Tugas'
  }

  /**
   * Set nomor surat to a specific value (for edit mode)
   * @param {string} value - The nomor surat value
   */
  const setNomorSurat = (value) => {
    nomorSurat.value = value
    baseNomorSurat.value = extractNumberFromNomorSurat(value)
  }

  /**
   * Initialize nomor surat for edit mode
   * Sets the current number as the baseline (minimum for decrement)
   * Allows incrementing to find available numbers
   * @param {string} value - The nomor surat value
   * @param {string} jenisSurat - The surat type
   */
  const setNomorSuratForEdit = (value, jenisSurat) => {
    if (!value) return

    currentJenisSurat.value = jenisSurat
    nomorSurat.value = value
    const currentNumber = extractNumberFromNomorSurat(value)
    baseNomorSurat.value = currentNumber
    initialNomorSurat.value = 1 // Allow decrementing down to 001 (not restricted to current number)
    nomorSuratIncremented.value = false // Start with not incremented
  }

  // Computed
  const canDecrement = computed(() => {
    return baseNomorSurat.value > initialNomorSurat.value
  })

  return {
    // State
    nomorSurat,
    baseNomorSurat,
    initialNomorSurat,
    latestNomorFromBackend,
    nomorSuratIncremented,
    loading,
    currentJenisSurat,

    // Computed
    canDecrement,

    // Methods
    getFormatCode,
    generateNomorSurat,
    extractNumberFromNomorSurat,
    fetchLatestNomorSurat,
    incrementNomorSurat,
    decrementNomorSurat,
    resetNomorSurat,
    setNomorSurat,
    setNomorSuratForEdit,
  }
}
